import React, { useState } from "react";

import { MessageIcon } from "../../assets/icons";
import { useDarkMode } from "../../context/DarkModeContext";
import { useAppDispatch, useAppSelector } from "../../hooks/hooks";
import { forgotPassword } from "../../features/store/authSlice";
import { Button } from "../Button";
import { Input } from "../Input";
import {
  ActionButtonsProps,
  FormContentProps,
  ResetPasswordFormProps,
} from "./types";

// Main Component
const ResetPasswordForm: React.FC<ResetPasswordFormProps> = ({
  isModal = false,
  onClose,
  onSignIn,
  onSuccess,
}) => {
  const [email, setEmail] = useState("");
  const { darkMode } = useDarkMode();
  const dispatch = useAppDispatch();

  // Get loading and error state from Redux
  const { loading, error } = useAppSelector((state) => state.auth);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await dispatch(forgotPassword({ email })).unwrap();
      if (onSuccess) onSuccess(); // Call onSuccess for modal flow
    } catch (error) {
      console.error("Failed to send reset email:", error);
    }
  };

  return (
    <div className="space-y-4">
      <FormContent
        email={email}
        setEmail={setEmail}
        onSubmit={handleSubmit}
        onClose={onClose}
        darkMode={darkMode}
        loading={loading}
        error={error}
      />
    </div>
  );
};

const ActionButtons: React.FC<ActionButtonsProps> = ({ onClose, loading }) => (
  <div className="flex gap-4">
    {onClose && (
      <Button
        onClick={onClose}
        bgColor="bg-gray-200 dark:bg-gray-800"
        className="flex-1"
        borderRadius="rounded-lg"
        disabled={loading}
      >
        Cancel
      </Button>
    )}
    <Button
      type="submit"
      bgColor="bg-gradient-to-r from-[#027EC2] to-[#039EF2]"
      textColor="text-white"
      className="flex-1 h-10"
      borderRadius="rounded-lg"
      disabled={loading}
    >
      {loading ? "Sending..." : "Submit"}
    </Button>
  </div>
);

const FormContent: React.FC<FormContentProps> = ({
  email,
  setEmail,
  onSubmit,
  onClose,
  darkMode,
  loading,
  error,
}) => (
  <form onSubmit={onSubmit} className="space-y-4">
    <Input
      type="email"
      placeholder="Email address"
      value={email}
      onChange={(e) => setEmail(e.target.value)}
      required
      icon={<MessageIcon theme={darkMode ? "dark" : "light"} />}
      disabled={loading}
    />

    {error && <div className="text-red-500 text-sm text-center">{error}</div>}

    <ActionButtons onClose={onClose} loading={loading} />
  </form>
);

export default ResetPasswordForm;